import { useCallback, useEffect, useMemo } from 'react';

import { useSearchParams } from 'react-router';

import type { PostTypeDataMap } from 'src/app/pages/starterDashboardV2/shared/hooks';

import type { PlatformType, RecommendationsData } from '../utils/platformGrouping';

import type { DisplayablePost } from './utils';
import { buildDisplayablePosts, computeAvailablePlatforms, getInitialPlatform } from './utils';

export type { DisplayablePost };

type UsePlatformFilterResult = ModelHookResult<
	{
		selectedPlatform: PlatformType | undefined;
		hasAnyValidContent: boolean;
		availablePlatforms: PlatformType[];
		displayablePosts: DisplayablePost[];
	},
	{
		selectPlatform: (platform: PlatformType) => void;
		clearPlatformSelection: () => void;
	}
>;

export function usePlatformFilter(
	postTypeData: PostTypeDataMap,
	recommendationsData: RecommendationsData,
	isMobile: boolean,
): UsePlatformFilterResult {
	const [searchParams, setSearchParams] = useSearchParams();
	const selectedPlatform = useMemo(() => getInitialPlatform(searchParams), [searchParams]);

	const selectPlatform = useCallback(
		(platform: PlatformType) => {
			const newSearchParams = new URLSearchParams(searchParams);
			newSearchParams.set('platform', platform);
			setSearchParams(newSearchParams, { replace: true });
		},
		[searchParams, setSearchParams],
	);

	const clearPlatformSelection = useCallback(() => {
		const newSearchParams = new URLSearchParams(searchParams);
		newSearchParams.delete('platform');
		setSearchParams(newSearchParams, { replace: true });
	}, [searchParams, setSearchParams]);

	const availablePlatforms = useMemo(
		() => computeAvailablePlatforms(postTypeData, recommendationsData),
		[postTypeData, recommendationsData],
	);

	useEffect(() => {
		if (selectedPlatform && !availablePlatforms.includes(selectedPlatform)) {
			const newSearchParams = new URLSearchParams(searchParams);
			newSearchParams.delete('platform');
			setSearchParams(newSearchParams, { replace: true });
		}
	}, [selectedPlatform, availablePlatforms, searchParams, setSearchParams]);

	const displayablePosts = useMemo(
		() => buildDisplayablePosts(selectedPlatform, availablePlatforms, { postTypeData, recommendationsData, isMobile }),
		[selectedPlatform, availablePlatforms, postTypeData, recommendationsData, isMobile],
	);

	return {
		data: {
			selectedPlatform,
			hasAnyValidContent: availablePlatforms.length > 0,
			availablePlatforms,
			displayablePosts,
		},
		actions: {
			selectPlatform,
			clearPlatformSelection,
		},
	};
}
